<script setup lang="ts">
import { ref, watch } from 'vue';
import { ArrowPathIcon } from '@heroicons/vue/24/outline';
import CopyButton from '@/components/aml-review/CopyButton.vue';
import TimestampWithIso from '@/components/ui/TimestampWithIso.vue';
import { dashedLabel } from '@/components/cards/cardDisplay';
import { useImmersveApiClient } from '@/hooks/useImmersveApiClient';
import type { FundingSource } from '@/integrations/Immersve/funding-sources/types';
import { balanceLabel } from './fundingSourceDisplay';

const props = defineProps<{ accountId: string }>();

const api = useImmersveApiClient();
const fundingSources = ref<FundingSource[]>([]);
const loading = ref(true);
const error = ref<Error | null>(null);

async function load() {
  loading.value = true;
  error.value = null;
  fundingSources.value = [];
  try {
    const { items } = await api.listFundingSources(props.accountId);
    fundingSources.value = [...items].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  } catch (err) {
    error.value = err instanceof Error ? err : new Error(String(err));
  } finally {
    loading.value = false;
  }
}

watch(() => props.accountId, load, { immediate: true });
</script>

<template>
  <div class="-mx-4 bg-white ring-1 ring-gray-300 sm:mx-0 sm:rounded-lg">
    <div
      v-if="loading"
      data-test="funding-sources-loading"
      class="flex items-center justify-center gap-2 px-4 py-6 text-sm text-gray-500"
    >
      <ArrowPathIcon class="h-4 w-4 animate-spin" />
      Loading…
    </div>
    <p v-else-if="error" data-test="funding-sources-error" class="px-4 py-6 text-sm text-red-600">
      {{ error.message }}
    </p>
    <p
      v-else-if="fundingSources.length === 0"
      data-test="funding-sources-empty"
      class="px-4 py-14 text-center text-sm text-gray-500"
    >
      This account has no funding sources.
    </p>
    <!-- Hashes don't fit side by side, so the IDs that belong to a funding
    source stack under its own as a labelled list. Below lg there is no room
    for columns at all, and every field joins that list. Row dividers stop
    short of the edges, as the first and last cells draw their own inset
    line. -->
    <table v-else class="min-w-full divide-y divide-gray-300">
      <thead class="hidden lg:table-header-group">
        <tr>
          <th
            scope="col"
            class="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-6"
          >
            Funding source
          </th>
          <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
            Balance
          </th>
          <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
            Purpose
          </th>
          <th
            scope="col"
            class="py-3.5 pr-4 pl-3 text-left text-sm font-semibold text-gray-900 sm:pr-6"
          >
            Created
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(fundingSource, index) in fundingSources"
          :key="fundingSource.id"
          data-test="funding-source-row"
        >
          <td
            :class="[
              index === 0 ? '' : 'border-t border-transparent',
              'relative py-4 pr-3 pl-4 text-sm sm:pl-6',
            ]"
          >
            <div class="flex min-w-0 items-center gap-1.5">
              <RouterLink
                :to="`/funding-sources/${encodeURIComponent(fundingSource.id)}`"
                class="font-mono font-medium break-all text-indigo-600 hover:text-indigo-700"
                data-test="funding-source-link"
              >
                {{ fundingSource.id }}
              </RouterLink>
              <CopyButton :value="fundingSource.id" label="funding source ID" />
            </div>
            <dl class="mt-2 space-y-1 text-xs">
              <div class="flex gap-x-3 lg:hidden">
                <dt class="w-16 shrink-0 text-gray-500">Balance</dt>
                <dd class="min-w-0 font-medium text-gray-900 tabular-nums">
                  {{ balanceLabel(fundingSource) }}
                </dd>
              </div>
              <div class="flex gap-x-3 lg:hidden">
                <dt class="w-16 shrink-0 text-gray-500">Purpose</dt>
                <dd class="min-w-0 text-gray-700 first-letter:uppercase">
                  {{ dashedLabel(fundingSource.purpose) }}
                </dd>
              </div>
              <div class="flex gap-x-3 lg:hidden">
                <dt class="w-16 shrink-0 text-gray-500">Network</dt>
                <dd class="min-w-0 text-gray-700">{{ fundingSource.network || '–' }}</dd>
              </div>
              <div class="flex gap-x-3">
                <dt class="w-16 shrink-0 text-gray-500">External</dt>
                <dd class="flex min-w-0 items-center gap-x-1.5 text-gray-700">
                  <span
                    class="break-all"
                    :class="{ 'font-mono': fundingSource.externalId }"
                    data-test="funding-source-external-id"
                  >
                    {{ fundingSource.externalId || '–' }}
                  </span>
                  <CopyButton :value="fundingSource.externalId" label="external ID" />
                </dd>
              </div>
              <div class="flex gap-x-3">
                <dt class="w-16 shrink-0 text-gray-500">Channel</dt>
                <dd class="flex min-w-0 items-center gap-x-1.5 text-gray-700">
                  <span
                    class="break-all"
                    :class="{ 'font-mono': fundingSource.fundingChannelId }"
                    data-test="funding-source-channel"
                  >
                    {{ fundingSource.fundingChannelId || '–' }}
                  </span>
                  <CopyButton :value="fundingSource.fundingChannelId" label="funding channel ID" />
                </dd>
              </div>
              <div class="flex gap-x-3 lg:hidden">
                <dt class="w-16 shrink-0 text-gray-500">Created</dt>
                <dd class="min-w-0"><TimestampWithIso :value="fundingSource.createdAt" /></dd>
              </div>
            </dl>
            <div
              v-if="index !== 0"
              class="absolute -top-px right-0 left-4 h-px bg-gray-200 sm:left-6"
            />
          </td>
          <td
            :class="[
              index === 0 ? '' : 'border-t border-gray-200',
              'hidden px-3 py-4 align-top text-sm whitespace-nowrap text-gray-900 tabular-nums lg:table-cell',
            ]"
            data-test="funding-source-balance"
          >
            {{ balanceLabel(fundingSource) }}
          </td>
          <td
            :class="[
              index === 0 ? '' : 'border-t border-gray-200',
              'hidden px-3 py-4 align-top text-sm whitespace-nowrap lg:table-cell',
            ]"
          >
            <div class="text-gray-900 first-letter:uppercase">
              {{ dashedLabel(fundingSource.purpose) }}
            </div>
            <div class="mt-1 text-xs text-gray-500">{{ fundingSource.network || '–' }}</div>
          </td>
          <td
            :class="[
              index === 0 ? '' : 'border-t border-transparent',
              'relative hidden py-4 pr-4 pl-3 align-top sm:pr-6 lg:table-cell',
            ]"
          >
            <TimestampWithIso :value="fundingSource.createdAt" />
            <div
              v-if="index !== 0"
              class="absolute -top-px right-4 left-0 h-px bg-gray-200 sm:right-6"
            />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
